import { Table } from '@/components';
import { A } from '@solidjs/router';
import type { Job } from '@uchat-team-management/shared';
import { Show } from 'solid-js';
import { JobResultBadge, MockBadge } from './StatusBadge';
import { dateTime, duration, secondsBetween, useNow } from './format';

const PAD_MS = 2 * 60_000;

/** Machine charts for the job's own window (padded a little on each side). */
export const resourcesHref = (j: Job, now: number): string | null => {
  if (!j.machineId || !j.startedAt) return null;
  const from = new Date(Date.parse(j.startedAt) - PAD_MS).toISOString();
  const to = new Date((j.completedAt ? Date.parse(j.completedAt) : now) + PAD_MS).toISOString();
  return `/runners/machines/${j.machineId}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
};

const link = 'text-brand-600 hover:underline dark:text-brand-400';

export const JobsTable = (props: { jobs: Job[]; loading?: boolean }) => {
  const now = useNow(1000);
  return (
    <Table
      data={props.jobs}
      loading={props.loading}
      rowKey={(j) => j.id}
      columns={[
        {
          key: 'started',
          header: 'Start → end',
          render: (j) => (
            <div class="text-xs tabular-nums">
              <div>{dateTime(j.startedAt)}</div>
              <div class="text-neutral-500">
                {j.completedAt ? dateTime(j.completedAt) : 'running'}
              </div>
            </div>
          ),
        },
        {
          key: 'job',
          header: 'Workflow / job',
          render: (j) => (
            <div class="min-w-0">
              <div class="flex items-center gap-2 font-medium">
                {j.jobName}
                <MockBadge source={j.source} />
              </div>
              <div class="text-xs text-neutral-500">
                {j.workflowName} · {j.repo}
                {j.headBranch ? ` · ${j.headBranch}` : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'runner',
          header: 'Runner',
          render: (j) => <span class="text-xs">{j.runnerName}</span>,
        },
        {
          key: 'duration',
          header: 'Duration',
          align: 'right',
          render: (j) => (
            <span class="tabular-nums">
              {duration(secondsBetween(j.startedAt, j.completedAt, now()))}
            </span>
          ),
        },
        {
          key: 'result',
          header: 'Result',
          render: (j) => <JobResultBadge status={j.status} conclusion={j.conclusion} />,
        },
        {
          key: 'links',
          header: '',
          render: (j) => (
            <span class="flex gap-3 text-xs whitespace-nowrap">
              <a
                class={link}
                href={`https://github.com/${j.repo}/actions/runs/${j.runId}`}
                target="_blank"
                rel="noreferrer"
              >
                Run
              </a>
              <a class={link} href={j.htmlUrl} target="_blank" rel="noreferrer">
                Logs
              </a>
              <Show when={resourcesHref(j, now())}>
                {(href) => (
                  <A class={link} href={href()} title="Machine resources during this job">
                    Resources
                  </A>
                )}
              </Show>
            </span>
          ),
        },
      ]}
    />
  );
};
